<template>
  <WithBackdrop
    :backdrop="data?.fields?.backdrop"
    :class="[$style.config, $style.wrap]"
  >
    <div :class="$style.inner">
      <header :class="$style.header">
        <BaseHeading as="h2" :class="$style.heading">
          {{ data?.fields?.header }}
        </BaseHeading>
      </header>
      <SearchField
        :class="$style.search"
        :input-class="$style.searchInput"
        :placeholder="`${localizedStrings['search-articles']}`"
        @request-query="handleSearch"
        @clear-query="handleSearch"
      />
      <div :class="$style.tags">
        <button
          v-for="tag in computedTags"
          :key="tag?.sys?.id"
          type="button"
          :class="[
            $style.tag,
            tag?.fields?.value === selectedTagFilter && $style.active,
          ]"
          :onclick="() => handleTagSelect(tag?.fields?.value as string)"
        >
          {{ tag?.fields?.label }}
        </button>
      </div>
    </div>
  </WithBackdrop>
</template>

<script setup lang="ts">
import type { TypeContentBlockWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { localizedStrings } = useLocalizedStrings()
const localePath = useLocalePath()

const props = defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

const selectedTagFilter = ref('')
const searchValue = ref('')

const computedTags = computed(() =>
  props.data?.fields?.references?.filter?.(instanceOfTag),
)

const handleSearch = (val: string) => {
  searchValue.value = val

  if (searchValue.value && searchValue.value.length) {
    const query: { [key: string]: string } = {
      q: searchValue.value,
      filter: 'article',
      sort: 'latest',
    }

    if (selectedTagFilter.value && selectedTagFilter.value.length) {
      query.tag = selectedTagFilter.value
    }

    navigateTo({
      path: localePath('/search'),
      query,
    })
  }
}
const handleTagSelect = (val: string) => (selectedTagFilter.value = val)
</script>

<style lang="scss" module>
.config {
  @include breakpoint-variables(
    '--as-wrap-padding',
    (
      base: 60px 20px,
      sm: 90px 20px,
      md: 120px 20px,
    )
  );
}

.wrap {
  padding: var(--as-wrap-padding);
}

.inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  max-width: 900px;
  margin: 0 auto;
}

.header {
  text-align: center;
  width: 100%;
  color: var(--white);

  @include media('md') {
    max-width: 800px;
  }
}

.heading {
  margin-bottom: 15px;
}

.search {
  width: 100% !important;
}

.searchInput {
  text-overflow: ellipsis;
}

.tags {
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  width: 100%;

  @include media('md') {
    justify-content: flex-start;
  }
}

.tag {
  all: unset;
  font-weight: 600;
  font-size: to-rem(16px);
  color: var(--white);
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  padding: to-rem(4px) to-rem(10px);
  transition: cubic-bezier(0.16, 1, 0.29, 0.99) 0.2s;

  &:hover,
  &.active {
    background: var(--purple);
  }
}
</style>
